import type { ITag } from 'services/api/entities';
import { TagType } from 'services/api/responses/responseTypes';
import { Nullable } from 'types';
import { Action, isActionOfType } from '../types';
import initialState from './initialState';
import type { TagStore, SortType } from './types';
import {
  resetTags,
  TagActionTypes,
  tagGetListSuccess,
  tagSelection,
  tagSelectionFromURLSuccess,
  updateFilters,
} from './actions';

const multiSelectables: Array<TagType> = ['moods', 'instruments', 'genres'];

const getTag = (tags: Record<string, ITag>, slug: string, type: string) =>
  Object.values(tags).find((tag) => tag.slug === slug && tag.type === type);

const selectTagsBySlugs = (
  tags: Record<string, ITag>,
  selectedTags: Record<string, Array<string>>
) => {
  const tagsToBeSelected: Record<string, Array<ITag>> = {};

  Object.entries(selectedTags).forEach(([tagType, selectedTagsName]) => {
    selectedTagsName.forEach((tagSlug: string) => {
      const tag = getTag(tags, tagSlug, tagType);
      if (tag) {
        if (!tagsToBeSelected[tagType]) {
          tagsToBeSelected[tagType] = [];
        }
        tagsToBeSelected[tagType].push(tag);
      }
    });
  });

  return tagsToBeSelected;
};

const isEqual = (arrayA: Array<SortType>, arrayB: Array<SortType>): boolean => {
  if (arrayA.length !== arrayB.length) return false;

  for (let i = 0; i < arrayA.length; i += 1) {
    if (!arrayB.includes(arrayA[i])) {
      return false;
    }
  }
  return true;
};

const isMultiSelect = (tagType: TagType): boolean =>
  multiSelectables.includes(tagType);

const replace = (
  array: Nullable<ReadonlyArray<ITag>>,
  tag: ITag
): ReadonlyArray<ITag> => {
  if (array?.includes(tag)) {
    return [];
  }
  return [tag];
};

const addOrRemove = (
  array: Nullable<ReadonlyArray<ITag>>,
  tag: ITag
): ReadonlyArray<ITag> => {
  if (array == null) return [tag];
  if (array.includes(tag)) {
    return array.filter((currentTag: ITag) => currentTag.id !== tag.id);
  }
  return [...array, tag];
};

const reducer = (
  state: TagStore = initialState,
  action: Action<TagActionTypes>
): TagStore => {
  if (isActionOfType(action, tagSelection)) {
    const { tag } = action.payload;
    const newSelectedTags = {
      ...state.selectedTags,
      [tag.type]: isMultiSelect(tag.type)
        ? addOrRemove(state.selectedTags[tag.type], tag)
        : replace(state.selectedTags[tag.type], tag),
    };
    return {
      ...state,
      // @ts-ignore
      selectedTags: newSelectedTags,
    };
  }

  if (isActionOfType(action, tagGetListSuccess)) {
    const { tags, tagIds, tagType } = action.payload;
    return {
      ...state,
      tags: {
        ...state.tags,
        ...tags,
      },
      [tagType]: [...tagIds],
    };
  }

  if (isActionOfType(action, tagSelectionFromURLSuccess)) {
    const { selectedTags } = action.payload;
    const tagsToBeSelected = selectTagsBySlugs(state.tags, selectedTags);
    return {
      ...state,
      selectedTags: tagsToBeSelected,
    };
  }

  if (isActionOfType(action, updateFilters)) {
    const { minDuration, maxDuration, sortBy } = action.payload;

    // To prevent multiple backend requests to the getSongs endopint with the same values
    // we need to check if the current filters differ from the old ones
    const currentMinDuration = state.appliedFilters.min_Duration;
    const currentMaxDuration = state.appliedFilters.max_Duration;
    const currentSort = state.appliedFilters.sort_by;
    if (
      currentMinDuration === minDuration &&
      currentMaxDuration === maxDuration &&
      // @ts-ignore
      isEqual(currentSort, sortBy)
    ) {
      return state;
    }

    return {
      ...state,
      appliedFilters: {
        // @ts-ignore
        min_Duration: minDuration,
        // @ts-ignore
        max_Duration: maxDuration,
        sort_by: sortBy,
      },
    };
  }

  if (isActionOfType(action, resetTags)) {
    return initialState;
  }

  return state;
};

export default reducer;
